/**
 * Plot UI bridge atoms. The renderer is imperative - uPlot
 * owns the canvas - but several Lit components need to reflect what was drawn:
 * the Plot button (loading/ETA), the stats strip (load time, missing), and the
 * `<plot-chart>` readout regions (x-window, filter summary, fit readout, color
 * legend, changeover legend). Those projections live here as atoms so the
 * imperative render path writes once and every interested component re-renders.
 *
 * Runtime caches that no component reads (the uPlot instance, lastData, leg
 * caches) stay non-reactive in `plot-runtime.ts`; only the *records of a draw*
 * that the UI must mirror are atoms.
 */

import { Atom } from '../../state/store.ts';
import type { AnyPlotResponse } from '../../api/types.ts';

/* ── Plot button (read by <plot-controls>) ──────────────── */

/** True while a `/plot` fetch is in flight (disables the Plot button). */
export const plotFetching = new Atom<boolean>(false);
/** "Loading… ~Xs" suffix shown in the Plot button, or null when idle. */
export const plotEta = new Atom<string | null>(null);
/**
 * Signature (`plotRequestSignature`) of the request that produced the chart on
 * screen - null when nothing is drawn or the last attempt failed. `<plot-controls>`
 * compares it against the request the Plot button would send now, so an unchanged
 * configuration disables the button instead of re-fetching an identical chart.
 */
export const plottedSignature = new Atom<string | null>(null);

/**
 * Ship drawn in the foreground by the `focus` colour mode; null means the first
 * plotted ship. Presentation only - changing it redraws from cached data.
 */
export const plotFocusShip = new Atom<string | null>(null);

/**
 * Which mount the current draw is using: the single uPlot root, or the grid of
 * per-ship panels.
 *
 * This has to be an atom rather than an inline style the renderer sets, because
 * `<plot-chart>` owns the `style` attribute on both mount points. Setting
 * `display` imperatively worked only until the next Lit render re-wrote that
 * attribute - and since `Atom.set` skips notifying on an unchanged value, the
 * re-render happened on the *first* grid draw (when `chartEmpty` actually
 * changed) but not the second, so grid appeared to need toggling twice.
 */
export const chartLayout = new Atom<'single' | 'grid'>('single');

/* ── Overlay fetches (read by <plot-controls> for the toggle spinners) ── */

/** True while the leg overlay is being fetched. */
export const legsLoading = new Atom<boolean>(false);
/** True while the changeover overlay is being fetched. */
export const changeoversLoading = new Atom<boolean>(false);

/* ── Last drawn data (read by export-stats for "(N shown)") ── */

export const lastPlotData = new Atom<AnyPlotResponse | null>(null);

/**
 * The data source `lastPlotData` came from - set with each fetch, not each
 * redraw, so switching source in the sidebar does not relabel a chart drawn
 * from the other one. Read by the Live chip and the last-value tags.
 */
export const plottedSource = new Atom<string>('historical');

/* ── Plot-tab stats strip (read by <export-stats>) ──────── */

export const plotLoadStat = new Atom<{ ms: number } | null>(null);

/**
 * What the drawn plot actually cost, per ship and in total.
 *
 * The Export tab's `Points` figure comes from `/estimate`, which is scoped to
 * the anchor ship and the export column selection - on the Plot tab it was
 * both the wrong number and a static one, since adding a comparison ship never
 * touched it. This is the plot's own count.
 */
export interface PlotPointStat {
  /** Points on screen after downsampling. */
  drawn: number;
  /** Rows the query read before downsampling. */
  scanned: number;
  perShip: Array<{ ship: string; drawn: number; scanned: number }>;
  /** Some requested panels failed, so totals cover only completed responses. */
  partial?: boolean;
}
export const plotPointStat = new Atom<PlotPointStat | null>(null);
export const plotMissingStat = new Atom<
  { pct: number; total: number; title: string } | null
>(null);

/* ── <plot-chart> readout regions ───────────────────────── */

/** The chart-container shows either the empty placeholder or the uPlot root. */
export interface ChartEmpty {
  text: string;
  sub: string;
}
export const DEFAULT_EMPTY: ChartEmpty = {
  text: 'No chart yet',
  sub: 'Choose a ship, configure the value or signals, and press Plot.',
};
/** Non-null → show the empty placeholder; null → show the chart root. */
export const chartEmpty = new Atom<ChartEmpty | null>(DEFAULT_EMPTY);

/** Server filter summary string (the component formats AND/OR + comparators). */
export const plotFilterSummary = new Atom<string | null>(null);

/** One row per fit, shown over the chart. */
export interface FitReadoutRow {
  series: string;
  color: string;
  eq?: string;
  r2?: number | null;
  error?: string;
}
export const fitReadout = new Atom<FitReadoutRow[]>([]);

/** Per-series fit label (eq + R²/error) shown under each `<series-picker>` chip. */
export interface FitLabel {
  eq?: string;
  r2?: number | null;
  error?: string;
}
export const fitLabels = new Atom<Record<string, FitLabel>>({});

/**
 * Continuous scatter color-by legend (gradient bar) shown below the chart, or
 * null when there's no color-by. Discrete (categorical) color-by draws its own
 * legend directly on the canvas (see `render-scatter.ts`), so it never uses this.
 */
export interface ColorLegend {
  colorBy: string;
  min: number;
  max: number;
  gradientCss: string;
}
export const colorLegend = new Atom<ColorLegend | null>(null);

/** Changeover legend strip data, or null when hidden. */
export interface ChangeoverLegend {
  groups: Array<{ key: string; display: string; solid: string }>;
  engines: string[];
  empty: boolean;
}
export const changeoverLegend = new Atom<ChangeoverLegend | null>(null);

/** Persistent x-axis window readout in the chart header. */
export interface XWindow {
  a: string;
  b: string;
  isZoomed: boolean;
}
export const plotXWindow = new Atom<XWindow | null>(null);

/** True once a chart has been drawn (gates the §10.10 image-export button). */
export const chartReady = new Atom<boolean>(false);
